import React, { useState } from "react";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { PageShell } from "../components/Layout";
import { pics } from "../components/Cards";
import "./Testimonials.css";

const reviews = [
  {
    name: "Amara Wilson",
    type: "Romantic getaway",
    quote:
      "The Bryan Residence made our anniversary feel truly special. The apartment was immaculate, elegant and incredibly peaceful.",
    initials: "AW",
  },
  {
    name: "Chidi Okafor",
    type: "Business stay",
    quote:
      "Luxury, comfort and intelligent living in one place. Everything was seamless from check-in to check-out.",
    initials: "CO",
  },
  {
    name: "Teni Adewale",
    type: "Family vacation",
    quote:
      "Our family felt completely at home. The thoughtful service and beautiful space made our Lagos stay unforgettable.",
    initials: "TA",
  },
  {
    name: "Lola Ibrahim",
    type: "Weekend stay",
    quote:
      "A calm, beautifully designed escape in the heart of Lekki. I cannot wait to return.",
    initials: "LI",
  },
  {
    name: "David Mensah",
    type: "Long stay",
    quote:
      "The perfect mix of privacy, modern technology and genuinely attentive hospitality.",
    initials: "DM",
  },
  {
    name: "Nneka James",
    type: "Celebration stay",
    quote:
      "Every request was handled with care. It felt like a luxury home made just for us.",
    initials: "NJ",
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const featured = reviews[current];
  const next = () => setCurrent((current + 1) % reviews.length);
  const previous = () =>
    setCurrent((current - 1 + reviews.length) % reviews.length);

  return (
    <PageShell>
      <main className="testimonials container">
        <section className="testimonials__hero">
          <div className="testimonials__hero-copy">
            <p className="eyebrow">Guest experiences at The Bryan Residence</p>
            <h1>
              Stories that
              <br />
              <em>stay with you.</em>
            </h1>
            <p>
              From family vacations and business trips to romantic getaways and
              celebrations, every stay tells a beautiful story.
            </p>
            <div className="testimonials__rating">
              <span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={14} fill="currentColor" />
                ))}
              </span>
              <b>4.9 / 5</b>
              <small>from our valued guests</small>
            </div>
          </div>

          <div className="testimonials__hero-gallery">
            <div
              className="testimonials__hero-image testimonials__hero-image--large"
              style={{ backgroundImage: `url(${pics[0]})` }}
            />
            <div
              className="testimonials__hero-image testimonials__hero-image--small"
              style={{ backgroundImage: `url(${pics[2]})` }}
            />
            <article>
              <span>“</span>
              <p>Luxury, warmth and thoughtful details in every corner.</p>
              <b>The Bryan Residence</b>
            </article>
          </div>
        </section>

        <section className="testimonials__feature">
          <div
            className="testimonials__feature-image"
            style={{ backgroundImage: `url(${pics[3]})` }}
          >
            <span>Featured guest story</span>
          </div>
          <article className="testimonials__feature-card">
            <div className="testimonials__feature-top">
              <span>Guest note 0{current + 1}</span>
              <div>
                {[1, 2, 3, 4, 5].map((x) => (
                  <Star key={x} size={13} fill="currentColor" />
                ))}
              </div>
            </div>
            <span className="testimonials__quote-mark">“</span>
            <p>{featured.quote}</p>
            <div className="testimonials__guest">
              <span>{featured.initials}</span>
              <div>
                <b>{featured.name}</b>
                <small>{featured.type}</small>
              </div>
            </div>
            <div className="testimonials__feature-controls">
              <button onClick={previous} aria-label="Previous story">
                ←
              </button>
              <span>
                {String(current + 1).padStart(2, "0")} /{" "}
                {String(reviews.length).padStart(2, "0")}
              </span>
              <button onClick={next} aria-label="Next story">
                <ArrowRight size={16} />
              </button>
            </div>
          </article>
        </section>

        <section className="testimonials__review-wall">
          <div className="testimonials__section-heading">
            <div>
              <p className="eyebrow">Words from our guests</p>
              <h2>
                Comfort, spoken
                <br />
                <em>beautifully.</em>
              </h2>
            </div>
            <p>
              Real experiences from guests who chose The Bryan Residence for
              moments that mattered.
            </p>
          </div>
          <div className="testimonials__review-grid">
            {reviews.map((review, index) => (
              <article key={review.name}>
                <span className="testimonials__review-number">
                  0{index + 1}
                </span>
                <div className="testimonials__review-stars">
                  {[1, 2, 3, 4, 5].map((x) => (
                    <Star key={x} size={11} fill="currentColor" />
                  ))}
                </div>
                <p>“{review.quote}”</p>
                <div className="testimonials__review-guest">
                  <span>{review.initials}</span>
                  <div>
                    <b>{review.name}</b>
                    <small>{review.type}</small>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="testimonials__metrics">
          <div>
            <b>
              4.9<small>/5</small>
            </b>
            <span>
              average guest
              <br />
              satisfaction score
            </span>
          </div>
          <div>
            <b>
              96<small>%</small>
            </b>
            <span>
              of guests would
              <br />
              recommend us
            </span>
          </div>
          <div>
            <b>
              1,745<small>+</small>
            </b>
            <span>
              memorable stays
              <br />
              and counting
            </span>
          </div>
        </section>

        <section className="testimonials__cta">
          <div>
            <p className="eyebrow">Your story begins here</p>
            <h2>
              Ready for an
              <br />
              <em>extraordinary stay?</em>
            </h2>
            <p>
              Experience luxury, comfort and intelligent living at The Bryan
              Residence, Lekki Phase 1.
            </p>
            <a href="/accommodation">
              Book your stay <ArrowUpRight size={16} />
            </a>
          </div>
          <div style={{ backgroundImage: `url(${pics[1]})` }}>
            <span>
              Luxury automated
              <br />
              holiday apartments
            </span>
          </div>
        </section>
      </main>
    </PageShell>
  );
}
